import React, { useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip as MapTooltip, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { X } from "lucide-react";

// ─── Map tiles ────────────────────────────────────────────────────────────────
// CARTO basemaps need a free key (https://carto.com/basemaps/) — without one every tile
// is an "API KEY REQUIRED" watermark. Until REACT_APP_CARTO_BASEMAPS_KEY is set we fall
// back to OpenStreetMap tiles darkened with a CSS filter (.ct-dark-tiles in index.css).
const CARTO_KEY = process.env.REACT_APP_CARTO_BASEMAPS_KEY;
const TILES = CARTO_KEY
    ? {
        url: `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_KEY)}`,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        className: "",
    }
    : {
        url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        subdomains: "abc",
        className: "ct-dark-tiles",
    };

const LEGEND = [["On Time","#10B981"],["Delayed","#F59E0B"],["Exception","#EF4444"],["Pending","#3B82F6"]];

// Selected outbound shipment (focus.state) → colour and wording
const FOCUS = {
    warehouse: { color: "#38BDF8", label: "In the warehouse" },
    transit:   { color: "#3B82F6", label: "In transit to customer" },
    delivered: { color: "#10B981", label: "Delivered" },
    issue:     { color: "#EF4444", label: "Shipped — needs action" },
};
const DC_COLOR = "#38BDF8";

// Zooms the map to the current pins whenever they change
function FitToPins({ points }) {
    const map = useMap();
    const sig = points.map(p => p.join(",")).join("|");
    useEffect(() => {
        if (points.length === 1) map.setView(points[0], 4);
        else if (points.length > 1) map.fitBounds(points, { padding: [30, 30], maxZoom: 5 });
    }, [sig, map]);
    return null;
}

function subtitle({ rawLocs, resolvedPins, routes, unmapped, hasExcel, activeView, viewMissing }) {
    if (rawLocs.length > 0) {
        const lanes = hasExcel && activeView !== "outbound" ? ` · ${routes.length} lane${routes.length !== 1 ? "s" : ""}` : "";
        const source = !hasExcel ? "live data"
            : activeView === "all" ? "both reports (outbound by receiving state)"
            : `the ${activeView} report${activeView === "outbound" ? " (by receiving state)" : ""}`;
        const missing = unmapped.length ? ` · not on map: ${unmapped.join(", ")}` : "";
        return `${resolvedPins.length} active location${resolvedPins.length !== 1 ? "s" : ""}${lanes} from ${source}${missing}`;
    }
    if (viewMissing) return `No ${activeView === "all" ? "" : `${activeView} `}report uploaded yet`;
    return "Showing default logistics hubs — add shipments with origin/destination to see live pins";
}

/**
 * @param dc     optional origin warehouse { name, address, coords } — always pinned (outbound)
 * @param focus  optional selected shipment { title, sub, state, destination: {name, coords} | null, note }
 */
export default function ShipmentMap(props) {
    const { resolvedPins, routes, hasExcel, dc, focus, onClearFocus, title = "Global Shipment Visibility", subtitleText, className = "lg:col-span-5", minHeight = 230 } = props;
    const f = focus ? FOCUS[focus.state] || FOCUS.transit : null;
    const fitPoints = focus && dc
        ? [dc.coords, ...(focus.destination ? [focus.destination.coords] : [])]
        : [...resolvedPins.map(p => p.coords), ...(dc ? [dc.coords] : [])];
    return (
        <div className={`${className} bg-[#111827] border border-[#1F2937] rounded-xl p-4 flex flex-col gap-3`}>
            <div className="flex items-center justify-between shrink-0">
                <div>
                    <p className="text-sm font-bold text-white">{title}</p>
                    <p className="text-[11px] text-slate-500">{subtitleText || subtitle(props)}</p>
                </div>
            </div>
            {focus && (
                <div className="flex items-start gap-2 rounded-lg border px-3 py-2 shrink-0" style={{ borderColor: `${f.color}55`, background: `${f.color}14` }}>
                    <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ background: f.color }} />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-white truncate">{focus.title} · <span style={{ color: f.color }}>{f.label}</span></p>
                        <p className="text-[11px] text-slate-400 truncate">{focus.sub}</p>
                        {focus.note && <p className="text-[10px] text-slate-500">{focus.note}</p>}
                        {focus.lead && <LeadTime lead={focus.lead} />}
                    </div>
                    {onClearFocus && <button onClick={onClearFocus} className="text-slate-500 hover:text-white" title="Clear selection"><X size={14} /></button>}
                </div>
            )}
            <div className="flex items-center gap-4 text-[11px] shrink-0">
                {LEGEND.map(([l,c]) => (
                    <span key={l} className="flex items-center gap-1.5 text-slate-400">
                        <span className="w-2 h-2 rounded-full" style={{backgroundColor:c}}/>{l}
                    </span>
                ))}
            </div>
            <div className="flex-1 rounded-lg overflow-hidden" style={{minHeight, isolation:"isolate"}}>
                <MapContainer
                    center={[15, 80]}
                    zoom={2}
                    minZoom={1}
                    maxZoom={8}
                    style={{height:"100%",minHeight,width:"100%"}}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution={TILES.attribution}
                        url={TILES.url}
                        subdomains={TILES.subdomains}
                        className={TILES.className}
                    />
                    <FitToPins points={fitPoints}/>
                    {routes.map((r, i) => (
                        <Polyline
                            key={i}
                            positions={r.positions}
                            color={r.color}
                            weight={r.weight}
                            opacity={hasExcel ? 0.55 : 0.35}
                            dashArray="6 4"
                        >
                            {r.label && (
                                <MapTooltip sticky>
                                    <div style={{fontSize:12,fontWeight:700}}>{r.label}</div>
                                    <div style={{fontSize:10,color:"#9CA3AF"}}>{r.count} PO line{r.count !== 1 ? "s" : ""}</div>
                                </MapTooltip>
                            )}
                        </Polyline>
                    ))}
                    {/* Selected shipment: DC → destination */}
                    {focus && dc && focus.destination && (
                        <Polyline positions={[dc.coords, focus.destination.coords]} color={f.color} weight={3} opacity={0.9}
                            dashArray={focus.state === "delivered" ? null : "8 6"}>
                            <MapTooltip sticky><div style={{fontSize:12,fontWeight:700}}>{focus.title}</div><div style={{fontSize:10,color:"#9CA3AF"}}>{f.label}</div></MapTooltip>
                        </Polyline>
                    )}
                    {focus && focus.destination && (
                        <CircleMarker center={focus.destination.coords} radius={9} color="#fff" weight={2} fillColor={f.color} fillOpacity={0.95}>
                            <MapTooltip direction="top" offset={[0,-8]} permanent={focus.state !== "warehouse"}>
                                <div style={{fontSize:12,fontWeight:700}}>{focus.destination.name}</div>
                                <div style={{fontSize:10,color:"#9CA3AF"}}>{focus.destination.detail ? `${focus.destination.detail} · ` : ""}city-level pin</div>
                            </MapTooltip>
                        </CircleMarker>
                    )}
                    {!focus && resolvedPins.map((pin, i) => (
                        <CircleMarker
                            key={i}
                            center={pin.coords}
                            radius={pin.count > 3 ? 10 : pin.count > 1 ? 7 : 5}
                            fillColor={pin.color}
                            color={pin.color}
                            weight={2}
                            opacity={0.9}
                            fillOpacity={0.55}
                        >
                            <MapTooltip direction="top" offset={[0,-8]}>
                                <div style={{fontSize:12,fontWeight:700}}>{pin.name}</div>
                                <div style={{fontSize:10,color:"#9CA3AF"}}>
                                    {pin.count} shipment{pin.count !== 1 ? "s" : ""}
                                </div>
                            </MapTooltip>
                        </CircleMarker>
                    ))}
                    {dc && (
                        <CircleMarker center={dc.coords} radius={focus?.state === "warehouse" ? 12 : 9} color="#fff" weight={2}
                            fillColor={focus?.state === "warehouse" ? f.color : DC_COLOR} fillOpacity={0.95}>
                            <MapTooltip direction="top" offset={[0,-8]} permanent={focus?.state === "warehouse"}>
                                <div style={{fontSize:12,fontWeight:700}}>{dc.name}</div>
                                <div style={{fontSize:10,color:"#9CA3AF"}}>{focus?.state === "warehouse" ? `${focus.title} is here` : dc.address}</div>
                            </MapTooltip>
                        </CircleMarker>
                    )}
                </MapContainer>
            </div>
        </div>
    );
}

// Lead time of the selected order: pick-up → delivery (days), and how long until delivery
const fmtDay = iso => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");
function LeadTime({ lead }) {
    const left = lead.left;
    const tone = lead.delivered ? "text-emerald-300" : left === null ? "text-slate-300" : left < 0 ? "text-red-400" : left <= 2 ? "text-amber-300" : "text-sky-300";
    return (
        <p className="text-[11px] text-slate-300 mt-1 flex flex-wrap items-center gap-x-2">
            <span className="font-semibold">Lead time{lead.days !== null ? ` ${lead.days} day${lead.days === 1 ? "" : "s"}` : ""}</span>
            <span className="text-slate-500">pick-up {fmtDay(lead.pickup)} → delivery {fmtDay(lead.delivery)}</span>
            <span className={`font-semibold ${tone}`}>
                {lead.delivered ? "Delivered" : left === null ? "" : left < 0 ? `${-left} day${left === -1 ? "" : "s"} overdue` : left === 0 ? "Due today" : `${left} day${left === 1 ? "" : "s"} to delivery`}
            </span>
        </p>
    );
}
